import { AlertTriangle, CalendarX2, Check } from 'lucide-react';

/**
 * La franja de conflictos: permanente, no un aviso que pasa.
 *
 * Un toast que dice "Juan queda al 200%" desaparece a los cinco segundos y con el desaparece el
 * problema. Un conflicto de carga **no es un evento, es un estado**: sigue estando mañana, y
 * mientras siga tiene que verse sin que nadie tenga que ir a buscarlo. Por eso vive abajo del
 * gráfico y ocupa lugar aunque no haya nada — que diga "nadie queda sobreasignado" es
 * información, y es la que hace creíble a la franja cuando sí dice algo.
 *
 * Las salidas de cada conflicto —repartir, posponer, reasignar— llegan con el arrastre: son
 * acciones que escriben, y escribir sin poder simular antes es lo que este rediseño saca.
 */

function fechaCorta(iso) {
  if (!iso) return '';
  const [, m, d] = String(iso).slice(0, 10).split('-');
  return `${d}/${m}`;
}

function cuando(c) {
  const dias = c.dias || [];
  if (dias.length <= 1) return fechaCorta(c.desde);
  return `${fechaCorta(c.desde)} → ${fechaCorta(c.hasta)} (${dias.length} d)`;
}

export default function GanttConflictos({ carga }) {
  const conflictos = carga?.conflictos || [];
  const fuera = carga?.fuera_del_calendario || [];

  if (!conflictos.length && !fuera.length) {
    return (
      <div className="border-t border-brand-border bg-zinc-50 px-3 py-2 flex items-center gap-2 text-[11px] text-zinc-500" data-testid="gantt-conflictos-limpio">
        <Check className="w-3 h-3" />
        Nadie queda sobreasignado con este plan.
      </div>
    );
  }

  return (
    <div className="border-t border-brand-border bg-[#FFFCF2] px-3 py-2 space-y-1.5 max-h-32 overflow-y-auto" data-testid="gantt-conflictos">
      {conflictos.map((c) => (
        <div key={`${c.persona_id}-${c.desde}`} className="flex items-center gap-2 text-[11px]">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 text-[#8A6400]" />
          <span className="font-mono font-medium">{c.persona_id}</span>
          <span className="text-zinc-400">·</span>
          <span>{cuando(c)}</span>
          <span className="text-zinc-400">·</span>
          <span className="font-mono text-brand-danger">{c.pico_pct}%</span>
          <span className="text-[10px] text-zinc-500 truncate">
            {(c.tareas || []).map((t) => t.title).filter(Boolean).join(' + ')}
          </span>
          {(c.tareas || []).some((t) => t.ajena) && (
            <span className="ml-auto shrink-0 text-[9px] uppercase tracking-wider text-zinc-500 border border-zinc-300 rounded-sm px-1">
              incluye otra cápsula
            </span>
          )}
        </div>
      ))}
      {fuera.map((t) => (
        <div key={t.task_id} className="flex items-center gap-2 text-[11px]">
          {/* La otra forma de tener un plan imposible: no es que haya más trabajo que día, es
              que ese día no existe para esa persona. No infla el porcentaje de nadie. */}
          <CalendarX2 className="w-3.5 h-3.5 shrink-0 text-[#8A6400]" />
          <span className="font-mono font-medium">{t.persona_id}</span>
          <span className="text-zinc-400">·</span>
          <span className="truncate">{t.title}</span>
          <span className="text-[10px] text-zinc-500 shrink-0">
            está planificada cuando no trabaja ({fechaCorta(t.planned_start)} → {fechaCorta(t.planned_end)})
          </span>
        </div>
      ))}
    </div>
  );
}
